import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import cx from 'classnames';

import { BinIcon, Button, DamageMissingTrimIcon } from '@motorway/mw-highway-code';

import { useFetch, useReport, REPORT_KEYS, REPORT_OUTCOMES, useSessionMode } from '@motorway/motorway-handshake-components';

import { clearSessionToken } from '../../api/helpers/auth.helpers';
import { BackButton } from '../../components/BackButton/BackButton';
import { Header } from '../../components/Header/Header';
import { debouncedAlert } from '../../helpers/debouncedAlert';
import { ROUTES } from '../../helpers/routes';
import { snowplowCtaClick } from '../../helpers/snowplow';

import styles from './Outcome.module.scss';

type ReportOutcomes = typeof REPORT_OUTCOMES[keyof typeof REPORT_OUTCOMES];

const SECTIONS = {
  CANCELLATION: 'cancellation',
  PAYMENT: 'payment',
} as const;

const OkIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M20.0351 15.89C19.8601 15.605 19.6251 15.365 19.3451 15.175C19.5001 15.02 19.6401 14.845 19.7551 14.65C20.2151 13.875 20.2351 12.94 19.8051 12.155C18.6251 9.99004 16.6051 8.64504 14.5301 8.64504C13.8901 8.64504 13.2401 8.67004 12.6251 8.79004L14.2601 5.52504C14.9001 4.38504 14.4951 2.93504 13.3551 2.29004C12.2151 1.65004 10.7651 2.05504 10.1251 3.19004L8.45508 6.09504V5.64504C8.45508 4.33504 7.39008 3.27004 6.08008 3.27004C4.77008 3.27004 3.70508 4.33504 3.70508 5.64504V15.945C3.70508 19.255 6.40008 21.95 9.71008 21.95L13.7051 21.965C17.6251 21.965 19.3201 20.17 20.0501 18.665C20.5301 17.68 20.5251 16.67 20.0351 15.89ZM6.08008 4.57004C6.67008 4.57004 7.15508 5.05004 7.15508 5.64504V8.35504L5.00508 12.085V5.64504C5.00508 5.05504 5.48508 4.57004 6.08008 4.57004ZM18.8801 18.095C18.0651 19.775 16.2751 20.665 13.7101 20.665L9.71008 20.65C7.11508 20.65 5.00508 18.54 5.00508 15.945C5.00508 15.12 5.22008 14.31 5.63508 13.6L11.2551 3.83504C11.4501 3.48504 11.8151 3.28504 12.1951 3.28504C12.3751 3.28504 12.5551 3.33004 12.7201 3.42004C13.2351 3.71004 13.4201 4.36504 13.1151 4.91004L10.6801 9.77004C10.5451 10.045 10.6151 10.375 10.8501 10.565C11.0851 10.755 11.4251 10.76 11.6651 10.565C12.3601 10.01 13.4551 9.93504 14.5301 9.93504C16.1051 9.93504 17.7301 11.045 18.6651 12.765C18.8751 13.15 18.8651 13.59 18.6351 13.975C18.4051 14.365 17.9951 14.6 17.5451 14.6C17.0951 14.6 16.6551 14.335 16.4301 13.915C15.8801 12.875 14.7701 12.275 13.5951 12.395C12.1001 12.545 11.0201 13.745 11.0201 15.25C11.0201 16.755 12.1251 17.985 13.5951 18.135C14.8051 18.26 15.9601 17.605 16.4801 16.515C16.6501 16.16 17.0301 15.93 17.4551 15.93C18.1351 15.93 18.6751 16.165 18.9301 16.575C19.1751 16.97 19.1601 17.505 18.8751 18.09L18.8801 18.095ZM15.8451 15.25C15.6251 15.45 15.4401 15.69 15.3101 15.96C15.0251 16.555 14.3951 16.91 13.7301 16.845C12.9151 16.765 12.3251 16.095 12.3251 15.255C12.3251 14.415 12.9151 13.775 13.7301 13.695C13.7851 13.69 13.8401 13.685 13.8901 13.685C14.4751 13.685 15.0051 14 15.2801 14.525C15.4251 14.805 15.6151 15.045 15.8401 15.25H15.8451Z" fill="white" />
  </svg>
);

export const Outcome = () => {
  const [nextStep, setNextStep] = useState<string | null>(null);
  const [selectedOutcome, setSelectedOutcome] = useState<ReportOutcomes | null>(null);
  const [submittingReport, setSubmittingReport] = useState(false);

  const { report, setReport } = useReport();
  const { patchReport, submitReport } = useFetch();
  const navigate = useNavigate();
  const { IS_FULL_SESSION, IS_LITE_SESSION } = useSessionMode();

  const { inspection: { outcome } } = report;

  const ROUTE_KEY = IS_FULL_SESSION ? 0 : 1;

  const TITLE = IS_LITE_SESSION ? 'Collection outcome' : 'Inspection outcome';
  const CTA_LABEL = IS_LITE_SESSION ? 'Confirm' : 'Next';

  const OPTION_LABELS = {
    adjustment: [
      'There were some issues, and we\'ve agreed to adjust the price.',
      'Purchased at adjusted price',
    ],
    cancellation: [
      'I\'d like to cancel the vehicle.',
      'Purchase cancelled',
    ],
    perfect: [
      'I\'d like to proceed with the original price.',
      'Purchased at original price',
    ],
  };

  const OPTIONS = [
    {
      icon: OkIcon,
      key: 'perfect',
      label: OPTION_LABELS.perfect[ROUTE_KEY],
      reportOutcome: REPORT_OUTCOMES.PERFECT,
      route: [ROUTES.SEND_REPORT, ROUTES.END],
      section: SECTIONS.PAYMENT,
    },
    {
      icon: DamageMissingTrimIcon,
      key: 'adjustment',
      label: OPTION_LABELS.adjustment[ROUTE_KEY],
      reportOutcome: REPORT_OUTCOMES.ADJUSTMENT,
      route: [ROUTES.PRICE_ADJUSTMENT, ROUTES.END],
      section: SECTIONS.PAYMENT,
    },
    {
      icon: BinIcon,
      key: 'cancellation',
      label: OPTION_LABELS.cancellation[ROUTE_KEY],
      reportOutcome: REPORT_OUTCOMES.CANCELLATION,
      route: [ROUTES.CANCELLATION, ROUTES.END],
      section: SECTIONS.CANCELLATION,
    },
  ];

  const renderOption = ({ key, icon, label, route, reportOutcome }: typeof OPTIONS[number]) => (
    <li key={key} className={cx({ [styles.selected]: selectedOutcome === reportOutcome })}>
      <Button
        fullWidth
        icon={icon}
        label={label}
        onClick={() => {
          const outcome = { [REPORT_KEYS.OUTCOME]: reportOutcome };

          setSelectedOutcome(reportOutcome);
          setReport({ inspection: { outcome: reportOutcome } });
          patchReport({ reportData: outcome });
          setNextStep(route[ROUTE_KEY]);

          snowplowCtaClick({
            button_label: label,
            label: reportOutcome,
            name: 'inspect_outcome_clicked',
            url: route[ROUTE_KEY],
          });
        }}
        reverse
        showLabel
        size="medium"
        type="button"
        variant="secondary"
      />
    </li>
  );

  return (
    <>
      <BackButton />
      <div className={styles.component}>
        <Header>
          <h1>{TITLE}</h1>
        </Header>
        {
          IS_LITE_SESSION
            ? (
                <>
                  <div className={styles.section}>
                    <p className={styles.divider}>Once the seller has received payment</p>
                    <ul>{OPTIONS.filter(({ section }) => section === SECTIONS.PAYMENT).map(renderOption)}</ul>
                    <p className={styles.divider}>Or</p>
                    <ul>{OPTIONS.filter(({ section }) => section === SECTIONS.CANCELLATION).map(renderOption)}</ul>
                  </div>
                </>
              )
            : <ul>{OPTIONS.map(renderOption)}</ul>
        }
        <div className={styles.button}>
          <Button
            disabled={!nextStep || submittingReport}
            fullWidth={IS_LITE_SESSION}
            icon="arrow"
            label={CTA_LABEL}
            loading={submittingReport}
            onClick={async () => {
              if (!nextStep) return;

              setSubmittingReport(true);

              snowplowCtaClick({
                button_label: CTA_LABEL,
                label: outcome,
                name: 'inspection_outcome_next',
                url: nextStep,
              });

              if (IS_FULL_SESSION) {
                navigate(nextStep);
                return;
              }

              const { success } = await submitReport();

              if (success) {
                navigate(nextStep, { replace: true });
                clearSessionToken();
                return;
              }

              debouncedAlert('There was an issue submitting your report. Please try again.');
              setSubmittingReport(false);
            }}
          />
        </div>
      </div>
    </>
  );
};
